import React from 'react';
import { GlobalOrdersRequest } from '@api/ringil-api.v2.tsx';
import ActionMenu from '@components/RingilComponents/ActionMenu.tsx';
import SvgShuffle from '@icons/shuffle.svg?react';
import SvgLoadUp from '@icons/LoadUp.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';

interface ComponentProps {
  locIndex: number;
  /** Orders loaded at other stops, i.e. the ones this stop could take over. */
  loadableOrders: GlobalOrdersRequest[];
  onMoveOrderLoading: (orderId: string, targetLocIndex: number) => void;
  dataTestIdPrefix?: string;
}

/**
 * Takes over the loading of an order that is currently picked up at another stop — the counterpart
 * to adding a brand new one, which is why it sits next to that button.
 *
 * `ActionMenu` renders nothing when there is nothing to take over, so the button disappears on a
 * route where every order is already here.
 */
const MoveExistingOrderButton: React.FC<ComponentProps> = ({ locIndex, loadableOrders, onMoveOrderLoading, dataTestIdPrefix }) => {
  const { tr } = useTranslationLgs();

  return (
    <ActionMenu
      mode={'outlined-rectangle'}
      size={'big'}
      icon={<SvgShuffle className={'size-4 shrink-0'} />}
      label={tr('CreateExpedition.moveExistingOrder', 'Move existing')}
      heading={tr('CreateExpedition.moveExistingOrderHeading', 'Load an order from another stop here')}
      dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-moveExistingOrder` : undefined}
      items={loadableOrders.map(order => ({
        key: order.id,
        label: order.orderName,
        icon: <SvgLoadUp />,
        onClick: () => onMoveOrderLoading(order.id, locIndex),
        dataTestId: dataTestIdPrefix ? `${dataTestIdPrefix}-moveExistingOrder-${order.id}` : undefined,
      }))}
    />
  );
};

export default MoveExistingOrderButton;